<template>
    <!-- 价格卡片 (4列) -->
    <div class="grid w-full max-w-[1136px] grid-cols-1 gap-3" :class="[isJPEnv ? 'md:grid-cols-3' : 'md:grid-cols-4']">
        <div
            v-for="plan in toValue(pricingPlans)"
            :key="plan.id"
            class="relative flex flex-1 flex-col items-center rounded-[20px] bg-white p-4 border border-[#DDEBF8]"
            :class="{'border-eureka-500 border-2': plan.isRecommended}"
        >
            <div v-if="plan.isRecommended" class="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-eureka-500 text-white text-xs font-semibold px-2 py-1 rounded-full">
                {{ t('recommend') }}
            </div>

            <!-- 计划名称 + 徽章 -->
            <div class="flex flex-col items-center justify-center gap-2 w-full h-[212px]">
                <div class="flex flex-col w-full items-start justify-around gap-2">
                    <h3 class="flex-1 text-xl font-semibold leading-[1.4] text-[#020A1A] whitespace-nowrap">
                        {{ t(`plans.${plan.id}.name`) }}
                    </h3>

                    <!-- Pro plan 的周期切换器 -->
                    <div v-if="plan.id === 'pro'" class="flex items-center gap-1 bg-[#F4F5F7] rounded-full p-0.5">
                        <button
                            :class="[
                                'px-3 py-1 rounded-full text-xs transition-all outline-none',
                                billingCycle === 'monthly'
                                    ? 'bg-white text-[#020A1A]'
                                    : 'bg-transparent text-[#020A1A]'
                            ]"
                            :style="billingCycle === 'monthly' ? 'box-shadow: 0px 0px 4px 0px #0764E924, 0px 4px 8px 0px #0764E914;' : ''"
                            @click="billingCycle = 'monthly'"
                        >
                            {{ t('billingCycle.monthly') }}
                        </button>
                        <button
                            :class="[
                                'px-3 py-1 rounded-full text-xs transition-all outline-none flex items-center gap-1',
                                billingCycle === 'yearly'
                                    ? 'bg-white text-[#020A1A]'
                                    : 'bg-transparent text-[#020A1A]'
                            ]"
                            :style="billingCycle === 'yearly' ? 'box-shadow: 0px 0px 4px 0px #0764E924, 0px 4px 8px 0px #0764E914;' : ''"
                            @click="billingCycle = 'yearly'"
                        >
                            {{ t('billingCycle.yearly') }}
                            <span class="text-[10px] text-[#008236] font-semibold">{{ t('billingCycle.saveAmount', { amount: `$${saveAmount}` }) }}</span>
                        </button>
                    </div>
                    <div v-else class="h-[28px]"></div>

                    <!-- <span
                        v-if="plan.badge"
                        class="flex items-center justify-center gap-1 rounded-[24px] bg-[#DCFCE7] px-2 py-1 text-xs leading-[1.33] text-[#008236]"
                    >
                        {{ plan.badge }}
                    </span> -->
                </div>

                <!-- 价格 -->
                <div class="flex flex-col gap-1 items-start w-full">
                    <div class="text-3xl font-semibold leading-[1.33] text-[#020A1A] flex flex-row flex-wrap items-end gap-1">
                        <template v-if="typeof plan.price === 'number'">
                            <div>{{ getDollarPriceDisplay(plan.price) }}</div>
                            <div class="text-sm font-normal text-[#8993A4] pb-2">/ {{ plan.priceUnit }}</div>
                        </template>
                        <template v-else>
                            {{ plan.price }}
                        </template>
                    </div>
                    <div
                        v-if="plan.priceNote"
                        class="text-xs leading-[1.33] text-[#8993A4]"
                    >
                        {{ plan.priceNote }}
                    </div>
                </div>

                <!-- 描述 -->
                <p class="h-15 text-sm leading-[1.43] text-grey-450 mt-4 w-full">
                    {{ t(`plans.${plan.id}.description`) }}
                </p>
            </div>

            <!-- CTA 按钮 -->
            <button
                :disabled="plan.disabled || (isProLoading && plan.id === 'pro')"
                :class="[
                    'flex items-center justify-center gap-2.5 rounded-[20px] px-7 py-2 text-base font-semibold leading-[1.5] transition-all w-full mt-6',
                    plan.disabled && !plan.isCurrent ? 'invisible' : '',
                    plan.disabled
                        ? 'bg-[#F4F5F7] cursor-not-allowed'
                        : plan.buttonStyle === 'primary'
                            ? 'bg-[#0764E9] text-white hover:bg-[#0456D1]'
                            : 'bg-[#020A1A] text-white hover:bg-[#364153]',
                    plan.disabled && plan.isCurrent ? 'text-[#020A1A]' : '',
                    plan.isRecommended && !plan.disabled ? 'bg-eureka-500 text-white hover:bg-eureka-600' : '',
                    isProLoading && plan.id === 'pro' ? 'opacity-70 cursor-not-allowed' : '',
                ]"
                @click="handlePlanAction(plan)"
            >
                <span v-if="isProLoading && plan.id === 'pro'" class="th-loading th-loading-spinner th-loading-sm"></span>
                {{ plan.isCurrent ? t('currentPlan') : getButtonText(plan.id) }}
            </button>

            <!-- 功能列表 -->
            <div class="flex flex-col justify-center gap-2 self-stretch mt-6">
                <!-- 支持分组功能(Enterprise)和普通功能列表(Basic/Pro/API) -->
                <template v-if="plan.id === 'enterprise' || plan.id === 'pro'">
                    <!-- featureGroupHeader: 只在 pro 和 enterprise 显示 -->
                    <div v-if="t(`plans.${plan.id}.featureGroupHeader`)" class="text-sm leading-[1.71] text-[#020A1A] font-semibold mb-2">
                        {{ t(`plans.${plan.id}.featureGroupHeader`) }}
                    </div>
                    <div
                        v-for="(group, groupIndex) in plan.featureGroups"
                        :key="groupIndex"
                        class="flex flex-col gap-2"
                    >
                        <!-- featureGroups 的 header 只在 enterprise 显示 -->
                        <div v-if="plan.id === 'enterprise'" class="text-sm leading-[1.71] text-[#020A1A] font-semibold">
                            {{ t(`plans.${plan.id}.featureGroups.${groupIndex}.header`) }}
                        </div>
                        <div
                            v-for="(_feature, featureIndex) in group.features"
                            :key="featureIndex"
                            class="flex gap-1 self-stretch"
                        >
                            <PtIcon icon="SolidTickCircle" class="text-2xl fill-[#0764E9] flex-shrink-0" />
                            <span class="text-sm leading-[1.43] text-grey-450">
                                {{ t(`plans.${plan.id}.featureGroups.${groupIndex}.features.${featureIndex}`) }}
                            </span>
                        </div>
                    </div>
                </template>
                <template v-else>
                    <div
                        v-for="(_feature, index) in plan.features"
                        :key="index"
                        class="flex gap-1 self-stretch"
                    >
                        <PtIcon icon="SolidTickCircle" class="text-2xl fill-[#0764E9] flex-shrink-0" />
                        <span class="text-sm leading-[1.43] text-[#495973]">
                            {{ t(`plans.${plan.id}.features.${index}`) }}
                        </span>
                    </div>
                </template>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import _ from 'lodash';
import { PtIcon, Toast } from 'patsnap-biz';
import '@patsnap-ui/icon/assets/solid/TickCircle.svg';
import { PLAN_CONFIG, useLsEditionUsage, usePlg, type LsEditionType } from '@eureka/shared/edition-upgrade';
import { EurekaPackageType } from '@eureka/services/src/apis/common.api.types';
import { openClueReport } from '@eureka/shared/clue-report';
import { useTrack } from '../../composables/use-track';
import { ref } from 'vue';
import { combineHash, combineUrl } from '@eureka/utils/url';
import { cn, en, jp, tw } from './locales';
import { PRICE_MAP } from '../../edition-upgrade/src/constants';
import { Site, SLD } from '@patsnap-utils/biz';
import { computedAsync, toValue } from '@vueuse/core';
import { lsPlanDeferred } from '@eureka/utils/ls-plan';

export interface FeatureGroup {
    header: string;
    features: Record<string, string>;
}

export interface PricingPlan {
    id: string;
    editionType?: LsEditionType | 'enterprise' | 'api';
    price?: number | string;
    originalPrice?: number | null;
    priceUnit: string;
    priceNote?: string;
    highlighted: boolean;
    buttonStyle: 'primary' | 'outline';
    badge: string | null;
    disabled: boolean;
    isRecommended: boolean;
    isCurrent: boolean;
    features?: string[];
    featureGroups?: FeatureGroup[];
}

interface Props {
    eventDetail?: Record<string, any>;
}

const props = defineProps<Props>();

const track = useTrack();

const {
    userEdition, createPaymentSession, checkHasActiveSubscription,
} = useLsEditionUsage()

const { isJPEnv } = usePlg();

// 计费周期：monthly 或 yearly
const billingCycle = ref<'monthly' | 'yearly'>('monthly')
const isProLoading = ref(false)

const { t } = useI18n({
    messages: {
        cn,
        en,
        jp,
        tw,
    },
});

const saveAmount = computedAsync<number>(async() => {
    const planValue = await lsPlanDeferred.promise;
    const planConfig = planValue === 'economy' ? PLAN_CONFIG.B : PLAN_CONFIG.A;
    return (planConfig.personal_pro.salePrice - (planConfig.personal_pro.yearlySalePrice || 0)) * 12;
}, 0);

// 定价计划配置
const pricingPlans = computedAsync<PricingPlan[]>(async() => {
    const currentRole = userEdition.value;
    const currentBillingCycle = billingCycle.value;
    const isJP = isJPEnv.value;

    const planValue = await lsPlanDeferred.promise;

    const planConfig = planValue === 'economy' ? PLAN_CONFIG.B : PLAN_CONFIG.A;

    const res: PricingPlan[] = _.compact([
        {
            id: 'basic',
            editionType: 'personal_basic',
            price: t('plans.basic.price'),
            originalPrice: null,
            priceUnit: t('month'),
            highlighted: false,
            buttonStyle: 'outline',
            badge: null,
            disabled: true,
            isRecommended: false,
            isCurrent: (currentRole === 'personal_basic'),
            features: ['0', '1', '2', '3'],
        },
        !isJP && {
            id: 'pro',
            editionType: 'personal_pro',
            price: currentBillingCycle === 'monthly'
                ? planConfig.personal_pro.salePrice
                : planConfig.personal_pro.yearlySalePrice,
            originalPrice: currentBillingCycle === 'monthly'
                ? planConfig.personal_pro.originalPrice
                : planConfig.personal_pro.yearlyOriginalPrice,
            priceUnit: currentBillingCycle === 'monthly' ? t('month') : t('month'),
            highlighted: false,
            buttonStyle: (currentRole === 'personal_basic') ? 'primary' : 'outline',
            badge: t('flashSale'),
            disabled: currentRole === 'personal_pro',
            isRecommended: currentRole === 'personal_basic',
            isCurrent: (currentRole === 'personal_pro'),
            featureGroups: [
                {
                    header: '',
                    features: { 0: '', 2: '', 3: '', 4: '' } as Record<string, string>,
                }
            ],
        },
        {
            id: 'enterprise',
            editionType: 'enterprise',
            price: t('plans.enterprise.price'),
            originalPrice: null,
            priceUnit: t('month'),
            priceNote: '',
            highlighted: false,
            buttonStyle: 'outline',
            badge: null,
            disabled: false,
            isRecommended: false,
            isCurrent: false,
            featureGroups: [
                {
                    header: '',
                    features: { 0: '', 1: '' } as Record<string, string>,
                },
                {
                    header: '',
                    features: { 0: '', 1: '', 2: '', 3: '' } as Record<string, string>,
                },
                {
                    header: '',
                    features: { 0: '', 1: '', 2: '', 3: '' } as Record<string, string>,
                }
            ],
        },
        {
            id: 'api',
            editionType: 'api',
            price: t('plans.api.price'),
            originalPrice: null,
            priceUnit: t('month'),
            priceNote: '',
            highlighted: false,
            buttonStyle: 'outline',
            badge: null,
            disabled: false,
            isRecommended: false,
            isCurrent: false,
            features: ['0', '1', '2', '3'],
        }
    ]);
    return res;
}, [] as PricingPlan[]);

// 选择计划
const handleSelectPlan = async(planId: Extract<LsEditionType, 'personal_pro'>) => {
    isProLoading.value = true;

    // 先检查是否已有订阅
    const hasActiveSubscription = await checkHasActiveSubscription().catch(() => false);
    if (hasActiveSubscription) {
        Toast({
            message: t('subscriptionExists'),
            type: 'warning',
            duration: 2000,
        });
        setTimeout(() => {
            window.location.reload();
        }, 2000);
        // loading 保持，页面即将 reload
        return;
    }
    const planValue = await lsPlanDeferred.promise;
    const priceMap = planValue === 'economy' ? PRICE_MAP.B : PRICE_MAP.A;

    const priceId = priceMap.personal_pro.find(item => item.billingCycle === billingCycle.value)?.priceId;
    if (!priceId) {
        isProLoading.value = false;
        return;
    }

    // success_url应该是在当前路径上加一个参数payment_success=true
    const currentUrl = window.location.href;
    const res = await createPaymentSession({
        price_ids: [priceId],
        mode: 'subscription',
        ui_mode: 'hosted',
        success_url: combineHash(combineUrl(location.origin, 'ls'), `user-setting/package/ls/plg/payment/success?redirectUrl=${encodeURIComponent(currentUrl)}`),
        cancel_url: combineUrl(location.origin, 'ls'),
        automatic_tax: true,
    }).catch(err => {
        console.error('[PlanDialog] createPaymentSession failed:', err);
        isProLoading.value = false;
    });

    // 添加埋点
    track({
        eventType: 'FF LS PLG',
        moduleType: 'LS',
        sourceType: `select-plan-${planId}`,
        otherParam: planId,
    });

    if (res?.url) {
        // loading 保持，页面即将跳转
        window.location.href = res.url;
    } else if (res !== undefined) {
        isProLoading.value = false;
    }
};

// 联系销售
const handleContactSales = () => {
    // 添加埋点
    track({
        eventType: 'FF LS PLG',
        moduleType: 'LS',
        sourceType: 'contact-sales-enterprise',
        otherParam: 'enterprise',
    });

    openClueReport({
        pkgType: EurekaPackageType.LS_COMMERCIAL,
        version: 'LS_PLG',
    });
};

// 处理计划操作
const handlePlanAction = (plan: PricingPlan) => {
    if (plan.disabled) return;

    if (plan.id === 'enterprise') {
        // Contact sales
        handleContactSales();
    } if (plan.id === 'api') {
        // 添加埋点
        track({
            eventType: 'FF LS PLG',
            moduleType: 'LS',
            sourceType: 'click-custom-api',
            otherParam: 'api',
        });

        window.location.href = new Site({ sld: SLD.OPEN, pathname: '?utm_medium=eureka_ls' }).href;
    } else {
        if (plan.editionType === 'personal_pro') {
            track({
                eventType: 'SS EUREKA UPGRADE PAGE',
                sourceType: 'click_subscribe_pro',
                moduleType: 'LS',
                eventDetail: {
                    product_line: 'eureka_ls',
                    price_type: billingCycle.value,
                    ...(props.eventDetail || {}),
                },
            })
            // Select plan
            handleSelectPlan(plan.editionType);
        }
    }
};

/**
 * 格式化美元价格显示
 */
function getDollarPriceDisplay(price: number): string {
    return `$${price}`;
}

/**
 * 获取按钮文本
 */
function getButtonText(planId: string): string {
    return t(`plans.${planId}.buttonText`);
}

</script>
